import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, DatePicker, Form, Input, Space, Switch, Table, Tabs, Tag, Typography, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { transportApi } from "../../api/transportApi";
import { getDefaultContext } from "../../api/httpClient";
import type { DriverDto } from "../../types/api";
import { getEnteredByUser } from "../../utils/currentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { SaveActionButton } from "../../shared/components/SaveActionButton";
import { EntityAttachmentsPanel } from "../../shared/components/EntityAttachmentsPanel";

export const DriverListPage = () => {
  const [rows, setRows] = useState<DriverDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [form] = Form.useForm();
  const [drawerTab, setDrawerTab] = useState("details");

  const load = async () => {
    setLoading(true);
    try {
      const result = await transportApi.listDrivers({ page: 1, pageSize: 500, q: search.trim() || undefined });
      setRows(result.data);
      setPage(1);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load drivers"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      row.driverName.toLowerCase().includes(term) ||
      row.mobile.toLowerCase().includes(term) ||
      row.licenseNo.toLowerCase().includes(term) ||
      row.address.toLowerCase().includes(term) ||
      (row.remarks ?? "").toLowerCase().includes(term)
    );
  }, [rows, search]);

  const onCreateOrUpdate = async () => {
    const values = await form.validateFields();
    const ctx = getDefaultContext();
    const payload = {
      ...ctx,
      enteredBy: getEnteredByUser(),
      driverName: values.driverName,
      mobile: values.mobile,
      licenseNo: values.licenseNo,
      licenseExpiry: values.licenseExpiry.toISOString(),
      address: values.address,
      isActive: values.isActive,
      remarks: values.remarks,
    };

    try {
      setSaving(true);
      if (editingId) {
        await transportApi.updateDriver(editingId, payload);
        message.success("Driver updated");
      } else {
        await transportApi.createDriver(payload);
        message.success("Driver added");
      }
      setOpen(false);
      setEditingId(null);
      form.resetFields();
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to save driver"));
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row: DriverDto) => {
    setDrawerTab("details");
    setEditingId(row.id);
    form.setFieldsValue({
      driverName: row.driverName,
      mobile: row.mobile,
      licenseNo: row.licenseNo,
      licenseExpiry: row.licenseExpiry ? dayjs(row.licenseExpiry) : undefined,
      address: row.address,
      isActive: row.isActive,
      remarks: row.remarks,
    });
    setOpen(true);
  };

  const onToggleActive = async (row: DriverDto) => {
    try {
      await transportApi.updateDriver(row.id, {
        organizationId: row.organizationId,
        financialYearId: row.financialYearId,
        enteredBy: getEnteredByUser(),
        driverName: row.driverName,
        mobile: row.mobile,
        licenseNo: row.licenseNo,
        licenseExpiry: row.licenseExpiry,
        address: row.address,
        isActive: !row.isActive,
        remarks: row.remarks,
      });
      message.success(`Driver marked ${row.isActive ? "inactive" : "active"}`);
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to update driver status"));
    }
  };

  return (
    <Card className="panel-card">
      <div className="panel-title-row">
        <Typography.Title level={3}>Driver Registry</Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingId(null);
            form.resetFields();
            form.setFieldsValue({ isActive: true });
            setOpen(true);
          }}
        >
          Add Driver
        </Button>
      </div>

      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search driver, mobile, license, address..."
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 360 }}
        />
        <Typography.Text type="secondary">
          Showing {filteredRows.length} of {rows.length} loaded drivers
        </Typography.Text>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredRows}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total: filteredRows.length,
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} drivers`,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
        columns={[
          {
            title: "#",
            width: 70,
            render: (_, __, index) => ((page - 1) * pageSize) + index + 1,
          },
          { title: "Driver", dataIndex: "driverName" },
          { title: "Mobile", dataIndex: "mobile", width: 130 },
          { title: "License No", dataIndex: "licenseNo", width: 160 },
          {
            title: "License Expiry",
            dataIndex: "licenseExpiry",
            width: 130,
            render: (value: string) => dayjs(value).format("DD MMM YYYY"),
          },
          { title: "Address", dataIndex: "address" },
          {
            title: "Status",
            width: 110,
            render: (_, row) => (
              <Tag color={row.isActive ? "green" : "default"} icon={row.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                {row.isActive ? "Active" : "Inactive"}
              </Tag>
            ),
          },
          {
            title: "",
            width: 180,
            render: (_, row) => (
              <Space size={6}>
                <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(row)}>
                  Edit
                </Button>
                <Button
                  size="small"
                  type={row.isActive ? "default" : "primary"}
                  icon={row.isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                  onClick={() => void onToggleActive(row)}
                >
                  {row.isActive ? "Inactive" : "Active"}
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <StaticDrawer
        title={editingId ? "Edit Driver" : "Add Driver"}
        open={open}
        onClose={() => { setOpen(false); setDrawerTab("details"); }}
        width={680}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => { setOpen(false); setDrawerTab("details"); }}>Cancel</Button>
            {drawerTab === "details" && (
              <SaveActionButton saving={saving} idleText={editingId ? "Update" : "Save"} savingText={editingId ? "Updating Driver..." : "Saving Driver..."} onClick={() => void onCreateOrUpdate()} />
            )}
          </div>
        }
        maskClosable={false}
      >
        <Tabs
          activeKey={drawerTab}
          onChange={setDrawerTab}
          size="small"
          style={{ marginTop: -8 }}
          items={[
            {
              key: "details",
              label: "Driver Details",
              children: (
                <Form form={form} layout="vertical" initialValues={{ isActive: true }}>
                  <Form.Item name="driverName" label="Driver Name" rules={[{ required: true, message: "Enter driver name" }]}>
                    <Input />
                  </Form.Item>

                  <Form.Item name="mobile" label="Mobile" rules={[{ required: true, message: "Enter mobile number" }]}>
                    <Input maxLength={20} />
                  </Form.Item>

                  <Form.Item name="licenseNo" label="License No" rules={[{ required: true, message: "Enter license number" }]}>
                    <Input maxLength={50} />
                  </Form.Item>

                  <Form.Item name="licenseExpiry" label="License Expiry" rules={[{ required: true, message: "Select license expiry" }]}>
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>

                  <Form.Item name="address" label="Address" rules={[{ required: true, message: "Enter address" }]}>
                    <Input.TextArea rows={3} />
                  </Form.Item>

                  <Form.Item name="isActive" label="Active" valuePropName="checked">
                    <Switch />
                  </Form.Item>

                  <Form.Item name="remarks" label="Remarks">
                    <Input.TextArea rows={3} />
                  </Form.Item>
                </Form>
              )
            },
            {
              key: "documents",
              label: "Documents",
              disabled: !editingId,
              children: (
                <EntityAttachmentsPanel
                  entityType="driver"
                  entityId={editingId}
                />
              )
            }
          ]}
        />
      </StaticDrawer>
    </Card>
  );
};
